<script lang="ts" setup>
export interface MapMarkersListProps {
    markers: MapMarker[]
}

const props = defineProps<MapMarkersListProps>()
const officesMapStore = useOfficesMapStore()
const { data, sendEvent } = useTracker()
const config = useRuntimeConfig()
const { $t } = useNuxtApp()

// Accessibility: Screen reader announcement for popup state changes (WCAG 4.1.2)
const popupAnnouncement = ref<string>('')

const handleSendEvent = (marker: MapMarker, type: string) => {
    const markerTitle = `${marker.data.address}, ${marker.data.zip_code} ${marker.data.city}`
    const title = data.title ? `${data.title} / ${markerTitle}` : markerTitle

    sendEvent({
        data: {
            ...data,
            title,
            click: type,
        },
    })
}

const closeMarker = (marker: MapMarker) => {
    // WCAG 2.4.3: Return focus to trigger element when popup closes
    const triggerElement = officesMapStore.value.triggerElement

    officesMapStore.value.activeMarker = undefined
    officesMapStore.value.activeOffice = undefined
    handleSendEvent(marker, 'close')

    // Announce popup closed (WCAG 4.1.2)
    popupAnnouncement.value = ''
    nextTick(() => {
        popupAnnouncement.value = $t('Informácie o pobočke zatvorené.') as string
    })

    // Return focus to the list item that opened this popup
    nextTick(() => {
        triggerElement?.focus()
    })
}

const openMarker = (marker: MapMarker): void => {
    officesMapStore.value.activeOffice = undefined
    officesMapStore.value.activeMarker = marker
    handleSendEvent(marker, 'open')

    // Announce popup opened with location info (WCAG 4.1.2)
    const markerTitle = `${marker.data.address}, ${marker.data.city}`
    popupAnnouncement.value = ''
    nextTick(() => {
        popupAnnouncement.value = `${$t('Otvorené informácie o pobočke')}: ${markerTitle}. ${$t('Použite Tab pre navigáciu, Escape pre zatvorenie.')}`
    })
}
</script>
<template>
  <!-- Screen reader announcement for popup state changes (WCAG 4.1.2) -->
  <Teleport to="body">
    <div
      aria-live="assertive"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ popupAnnouncement }}
    </div>
  </Teleport>
  <GMapMarker
    v-for="(marker, index) in props.markers"
    :key="`mm-${index}`"
    :z-index="
      officesMapStore.activeMarker?.data?.id === marker?.data?.id ||
        officesMapStore.hoveredMarker?.data.id === marker?.data?.id
        ? 10
        : 0
    "
    :icon="
      officesMapStore.activeMarker?.data?.id === marker?.data?.id ||
        officesMapStore.hoveredMarker?.data?.id === marker?.data?.id
        ? `${config.app.cdnURL}/${config.public.PUBLIC_IMAGES_URL}/pin-secondary-2.svg?v=1`
        : `${config.app.cdnURL}/${config.public.PUBLIC_IMAGES_URL}/pin-black-2.svg?v=1`
    "
    :position="{ lat: marker.lat, lng: marker.lng }"
    :title="marker.title"
    :clickable="true"
    :draggable="false"
    @click="openMarker(marker)"
  >
    <GMapInfoWindow
      :opened="
        officesMapStore.activeMarker?.data?.id === marker?.data?.id
      "
    >
      <MapModal @close="closeMarker(marker)">
        <slot
          name="modal"
          :data="marker.data"
        />
      </MapModal>
    </GMapInfoWindow>
  </GMapMarker>
</template>
